import React, { useEffect, useRef, useState } from "react";
import { Send, WifiOff, RotateCcw } from "lucide-react";
import { Header } from "@/components/Header";
import { ChatBubble } from "@/components/ChatBubble";
import { aiApi } from "@/lib/ai";

const SESSION_KEY = "opg_ai_session";
const SUGGESTIONS = ["Aaj kitna paisa aaya?", "Is hafte ki bikri ka saaraansh do", "Kaunsa payment method sabse zyada use hua?", "Bikri badhane ke tips do"];

const newSession = () => {
  const id = `ai_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  localStorage.setItem(SESSION_KEY, id);
  return id;
};

export default function Assistant() {
  const [sessionId, setSessionId] = useState(() => localStorage.getItem(SESSION_KEY) || newSession());
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [online, setOnline] = useState(navigator.onLine);
  const endRef = useRef(null);

  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);

  useEffect(() => {
    aiApi.history(sessionId).then((m) => setMsgs(m.map(({ role, text }) => ({ role, text })))).catch(() => {});
  }, [sessionId]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [msgs]);

  const send = async (text) => {
    const message = (text ?? input).trim();
    if (!message || busy) return;
    setInput("");
    setBusy(true);
    setMsgs((m) => [...m, { role: "user", text: message }, { role: "assistant", text: "", pending: true }]);
    const patchLast = (fn) => setMsgs((m) => [...m.slice(0, -1), fn(m[m.length - 1])]);
    try {
      await aiApi.stream({ sessionId, message, onDelta: (d) => patchLast((l) => ({ ...l, text: l.text + d })) });
    } catch (e) {
      patchLast((l) => ({ ...l, text: l.text || `⚠️ ${e.message}` }));
    } finally {
      patchLast((l) => ({ ...l, pending: false }));
      setBusy(false);
    }
  };

  const reset = () => {
    setMsgs([]);
    setSessionId(newSession());
  };

  return (
    <div className="page-shell flex flex-col min-h-screen" data-testid="assistant-page">
      <Header
        title="AI Assistant"
        subtitle="Ask about your sales · powered by Claude"
        badge={false}
        right={
          <button onClick={reset} className="p-2 rounded-full btn-outline-gold" aria-label="New chat" data-testid="ai-new-chat-btn">
            <RotateCcw size={16} />
          </button>
        }
      />

      {!online && (
        <div className="mb-4 rounded-xl px-4 py-3 text-xs flex items-center gap-2 border gold-border text-[#E6B455] bg-[rgba(230,180,85,0.06)]" data-testid="ai-offline-banner">
          <WifiOff size={14} /> AI needs internet. Your offline payments still work normally.
        </div>
      )}

      <div className="flex-1 space-y-4 pb-44" data-testid="ai-messages">
        {msgs.length === 0 && (
          <div className="fade-slide-up" data-testid="ai-empty">
            <p className="text-sm text-[#F7E6B4]/60 mb-4">Namaste! Main aapki bikri ke baare mein madad kar sakta hoon. Kuch poochhiye:</p>
            <div className="flex flex-wrap gap-2">
              {SUGGESTIONS.map((s, i) => (
                <button key={s} onClick={() => send(s)} disabled={!online} className="btn-outline-gold rounded-full px-3.5 py-2 text-xs" data-testid={`ai-suggestion-${i}`}>
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
        {msgs.map((m, i) => (
          <ChatBubble key={i} index={i} {...m} />
        ))}
        <div ref={endRef} />
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          send();
        }}
        className="fixed left-0 right-0 bottom-[104px] px-3 z-20"
      >
        <div className="mx-auto max-w-[560px] flex items-center gap-2 card-glass gold-border rounded-2xl p-2" style={{ backdropFilter: "blur(18px)" }}>
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={online ? "Apna sawaal likhiye..." : "Offline - AI unavailable"}
            disabled={!online || busy}
            maxLength={2000}
            className="flex-1 bg-transparent outline-none text-sm text-[#F7E6B4] placeholder:text-[#F7E6B4]/35 px-3 py-2"
            data-testid="ai-input"
          />
          <button type="submit" disabled={!online || busy || !input.trim()} className="btn-gold rounded-xl w-10 h-10 flex items-center justify-center disabled:opacity-40" data-testid="ai-send-btn" aria-label="Send">
            <Send size={16} />
          </button>
        </div>
      </form>
    </div>
  );
}
